import { PageOptions } from '@graphcommerce/framer-next-pages'
import { ApolloQueryResult, gql, mergeDeep } from '@graphcommerce/graphql'
import {
  AddProductsToCartButton,
  AddProductsToCartError,
  AddProductsToCartForm,
  AddProductsToCartFormProps,
  AddProductsToCartQuantity,
  getProductStaticPaths,
  jsonLdProduct,
  jsonLdProductOffer,
  ProductCustomizable,
  productPageCategory,
  ProductPageDescription,
  ProductPageMeta,
  ProductPagePrice,
  ProductPagePriceTiers,
  ProductShortDescription,
  ProductSidebarDelivery,
} from '@graphcommerce/magento-product'
import { BundleProductOptions } from '@graphcommerce/magento-product-bundle'
import {
  ConfigurableName,
  ConfigurablePrice,
  ConfigurablePriceTiers,
  ConfigurableProductOptions,
  ConfigurableProductPageGallery,
  defaultConfigurableOptionsSelection,
} from '@graphcommerce/magento-product-configurable'
import { DownloadableProductOptions } from '@graphcommerce/magento-product-downloadable'
import { jsonLdProductReview, ProductReviewChip } from '@graphcommerce/magento-review'
import { redirectOrNotFound, Money, StoreConfigDocument } from '@graphcommerce/magento-store'
import { ProductWishlistChipDetail } from '@graphcommerce/magento-wishlist'
import {
  GetStaticProps,
  JsonLd,
  LayoutHeader,
  LayoutTitle,
  isTypename,
} from '@graphcommerce/next-ui'
import arrowDown from '@graphcommerce/next-ui/icons/arrow-down.svg'
import { Trans } from '@lingui/react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material'
import groupBy from 'lodash/groupBy'
import { GetStaticPaths } from 'next'
import { useState } from 'react'
import {
  LayoutNavigation,
  LayoutNavigationProps,
  RowProduct,
  RowRenderer,
  Usps,
} from '../../components'
import { CustomConfigurablePrice } from '../../components/Layout/CustomConfigurablePrice'
import { CustomConfigurableProductOptions } from '../../components/Layout/CustomConfigurableProductOptions'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import getRevalidateTime from '../../constants'
import { ProductPage2Document, ProductPage2Query } from '../../graphql/ProductPage2.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'

type Props = ProductPage2Query &
  Pick<AddProductsToCartFormProps, 'defaultValues'> & { variants: any }

type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

const SHOW_WHOLE_TIER_PRICES = false
const ENABLE_TIER_PRICE_TABLE = false

const variantsQuery = gql`
  query Variants($urlKey: String!, $reviewPageSize: Int = 3, $reviewPage: Int = 1) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        uid
        ... on ConfigurableProduct {
          variants {
            attributes {
              uid
              label
              code
              value_index
            }
            product {
              name
              uid
              price_tiers {
                discount {
                  amount_off
                  percent_off
                }
                final_price {
                  currency
                  value
                }
                quantity
              }
            }
          }
        }
      }
    }
  }
`

function ProductPage(props: Props) {
  const { products, relatedUpsells, usps, sidebarUsps, pages, defaultValues } = props

  const product = mergeDeep(products, relatedUpsells)?.items?.[0]
  const reviewCountMicroData = product?.review_count ? jsonLdProductReview(product) : {}

  if (!product?.sku || !product.url_key) return null
  const variants = props?.variants?.items?.[0]?.variants

  const aggregate = ({ product: variant }) => JSON.stringify(variant.price_tiers)
  const groupedVariants = groupBy(variants, aggregate)
  if (groupedVariants?.hasOwnProperty('[]')) delete groupedVariants['[]']

  return (
    <>
      <LayoutHeader floatingMd>
        <LayoutTitle size='small' component='span'>
          {product.name}
        </LayoutTitle>
      </LayoutHeader>
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          ...jsonLdProduct(product),
          ...jsonLdProductOffer(product),
          ...reviewCountMicroData,
        }}
      />
      <ProductPageMeta {...product} />

      <AddProductsToCartForm key={product.uid} defaultValues={defaultValues}>
        <ConfigurableProductPageGallery
          url_key={product.url_key}
          media_gallery={product.media_gallery}
          sx={(theme) => ({
            '& .SidebarGallery-sidebar': { display: 'grid', rowGap: theme.spacings.sm },
          })}
        >
          <div>
            {isTypename(product, ['ConfigurableProduct', 'BundleProduct']) && (
              <Typography component='div' variant='body2' color='text.disabled' />
            )}

            <Typography variant='h3' component='div' gutterBottom>
              {product.name}
            </Typography>
            {/* <Typography component='div' variant='body2' color='text.disabled'>
              Cod. {product?.sku}
            </Typography> */}

            <ProductShortDescription
              short_description={product?.short_description}
              sx={(theme) => ({ mb: theme.spacings.xs })}
            />

            <ProductReviewChip rating={product.rating_summary} reviewSectionId='reviews' />
          </div>

          {isTypename(product, ['ConfigurableProduct']) && (
            <CustomConfigurableProductOptions
              product={product}
              optionEndLabels={{
                size: (
                  <Link
                    href='/modal/product/global/size'
                    rel='nofollow'
                    color='primary'
                    underline='hover'
                  >
                    <Trans id='Which size is right?' />
                  </Link>
                ),
              }}
            />
          )}
          {isTypename(product, ['BundleProduct']) && (
            <BundleProductOptions product={product} layout='stack' />
          )}
          {isTypename(product, ['DownloadableProduct']) && (
            <DownloadableProductOptions product={product} />
          )}
          {!isTypename(product, ['GroupedProduct']) && <ProductCustomizable product={product} />}

          <Divider />

          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              columnGap: theme.spacings.xs,
            })}
          >
            <AddProductsToCartQuantity sx={{ flexShrink: '0' }} />

            <AddProductsToCartError>
              <Typography component='div' variant='h3' lineHeight='1' whiteSpace='nowrap'>
                {/* {isTypename(product, ['ConfigurableProduct']) ? (
                  <CustomConfigurablePrice product={product} />
                ) : (
                  <ProductPagePrice product={product} />
                )} */}
                {isTypename(product, ['ConfigurableProduct']) ? (
                  <CustomConfigurablePrice product={product} />
                ) : (
                  <ProductPagePrice product={product} />
                )}
              </Typography>
              <Typography component='div' variant='body1'>
                <Trans id='addIva' />
              </Typography>
            </AddProductsToCartError>
          </Box>

          {isTypename(product, ['ConfigurableProduct']) ? null : ( // <ConfigurablePriceTiers product={product} />
            <ProductPagePriceTiers product={product} />
          )}

          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'start',
              columnGap: theme.spacings.xs,
            })}
          >
            <AddProductsToCartButton fullWidth product={product} />
            <ProductWishlistChipDetail {...product} />
          </Box>

          {variants && (
            <Stack direction='column' bgcolor='rgba(73, 25, 61, 0.1)' spacing={0} borderRadius={4}>
              {variants[0]?.product?.price_tiers?.map(
                ({ quantity, final_price, discount }, index) => (
                  <Box
                    key={quantity}
                    borderBottom={
                      index === variants[0]?.product?.price_tiers.length - 1
                        ? ''
                        : '1px solid #e5e5e5'
                    }
                    px={2}
                    py={1}
                    fontSize={15}
                  >
                    <strong>Sconto</strong> del <strong>{discount.percent_off}%</strong> per
                    <strong>{quantity} articoli</strong> o più&nbsp;
                  </Box>
                ),
              )}
            </Stack>
          )}
          {ENABLE_TIER_PRICE_TABLE && (
            <TableContainer component={Paper}>
              <Table
                aria-label='tier price table'
                sx={{ backgroundColor: 'rgba(73, 25, 61, 0.1)', borderRadius: '1rem' }}
              >
                <TableHead sx={{ borderRadius: '1rem' }}>
                  <TableRow>
                    <TableCell>Opzioni</TableCell>
                    <TableCell>Pz</TableCell>
                    <TableCell>Sconto</TableCell>
                    <TableCell>Prezzo</TableCell>
                    <TableCell>Pz</TableCell>
                    <TableCell>Sconto</TableCell>
                    <TableCell>Prezzo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedVariants &&
                    Object.values(groupedVariants)?.map((group) => {
                      if (SHOW_WHOLE_TIER_PRICES)
                        return group?.map(({ product, attributes }, index) => (
                          <TableRow>
                            <TableCell>
                              <Typography variant='body2'>
                                {attributes?.map(({ label }) => label).join(', ')}
                              </Typography>
                            </TableCell>
                            {product?.price_tiers?.map(
                              ({ quantity, final_price, discount }, index) => (
                                <>
                                  <TableCell>{quantity}</TableCell>
                                  <TableCell>
                                    <Tooltip
                                      title={<Money {...final_price} />}
                                      arrow
                                      placement='top'
                                    >
                                      <Typography variant='body2'>
                                        {discount.percent_off}%
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                </>
                              ),
                            )}
                          </TableRow>
                        ))
                      return (
                        <TableRow sx={{ borderBottom: '1px solid white' }}>
                          <TableCell>
                            <Tooltip
                              title={
                                <Typography>
                                  {group
                                    ?.map(({ attributes }) =>
                                      attributes?.map(({ label }) => label).join(' - '),
                                    )
                                    .join(', ')}
                                </Typography>
                              }
                              arrow
                              placement='top'
                            >
                              <Typography variant='body2'>
                                {`${group[0]?.attributes
                                  ?.map(({ label }) => label)
                                  .join(', ')} e altre ${group?.length - 1} varianti`}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          {group[0]?.product?.price_tiers?.map(
                            ({ quantity, final_price, discount }, index) => (
                              <>
                                <TableCell>Compra più di {quantity}</TableCell>
                                <TableCell>
                                  <Tooltip title={<Money {...final_price} />} arrow placement='top'>
                                    <Typography
                                      variant='body2'
                                      sx={{ color: 'rgba(73, 25, 61)', fontWeight: 'bold' }}
                                    >
                                      {discount.percent_off}%
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                {/* <TableCell>
                                <Money {...final_price} />
                              </TableCell> */}
                              </>
                            ),
                          )}
                        </TableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* {groupedVariants &&
            Object.values(groupedVariants)?.map((group) => (
              <Stack
                direction='column'
                bgcolor='rgba(73, 25, 61, 0.1)'
                spacing={0}
                borderRadius={4}
              >
                {group[0]?.product?.price_tiers?.map(
                  ({ quantity, final_price, discount }, index) => (
                    <Box
                      key={quantity}
                      borderBottom={
                        index === group[0]?.product?.price_tiers.length - 1
                          ? ''
                          : '1px solid #e5e5e5'
                      }
                      px={2}
                      py={1}
                      fontSize={16}
                    >
                      <Trans
                        id='Buy {quantity} for <0/> and save {percent}%'
                        components={{
                          0: (
                            <span style={{ color: 'rgba(73, 25, 61)', fontWeight: 'bold' }}>
                              <Money {...final_price} />
                            </span>
                          ),
                        }}
                        values={{ quantity, percent: discount.percent_off }}
                      />
                    </Box>
                  ),
                )}
                <Typography variant='body2' p={1} px={2}>
                  Valido per la varianti:&nbsp;
                  {group
                    ?.map(({ product: variant }) => variant?.name?.replace(`${product?.name}-`, ''))
                    .join(', ')}
                </Typography>
              </Stack>
            ))} */}
          <Usps usps={sidebarUsps} size='small' />
        </ConfigurableProductPageGallery>
      </AddProductsToCartForm>

      <ProductPageDescription {...product} right={<Usps usps={usps} />} fontSize='18px' />

      {pages?.[0] && (
        <RowRenderer
          content={pages?.[0].content}
          renderer={{
            RowProduct: (rowProps) => (
              <RowProduct
                {...rowProps}
                {...product}
                items={products?.items}
                aggregations={products?.aggregations}
              />
            ),
          }}
        />
      )}
    </>
  )
}

ProductPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default ProductPage

const TRIGGER_REINDEX_THRESHOLD = 1000

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  // if (import.meta.graphCommerce.legacyProductRoute) return { paths: [], fallback: false }
  // if (process.env.NODE_ENV === 'development') return { paths: [], fallback: 'blocking' }

  const getConfigurableProductPaths = (locale: string) =>
    getProductStaticPaths(graphqlSsrClient(locale), locale, 'ConfigurableProduct')

  const getSimpleProductPaths = (locale: string) =>
    getProductStaticPaths(graphqlSsrClient(locale), locale, 'SimpleProduct')

  const configurableProductPaths = (
    await Promise.all(locales.map(getConfigurableProductPaths))
  ).flat(1)

  const simpleProductsPaths = (await Promise.all(locales.map(getSimpleProductPaths))).flat(1)

  const allPathsExceptVariants = simpleProductsPaths.filter(
    (simpleProductPath) =>
      // if (typeof simpleProductPath === 'string' || !simpleProductPath?.params?.url) return false

      !configurableProductPaths.find((configurableProductUrl) =>
        // if (typeof configurableProductUrl === 'string') {
        //   return simpleProductPath?.params?.url.includes(configurableProductUrl)
        // }
        simpleProductPath?.params?.url.includes(configurableProductUrl?.params?.url),
      ),
  )

  if (allPathsExceptVariants.length + simpleProductsPaths.length < TRIGGER_REINDEX_THRESHOLD) {
    const res = await fetch('http://api.jode.it:8080/force-reindex')
    const text = await res.text()
    throw new Error(
      `[ERROR] FAILED BUILD - Triggering reindex as limit is not reached ${
        allPathsExceptVariants.length + simpleProductsPaths.length
      }, should appear another build in less than 120s. Response content: ${text}`,
    )
  }
  return {
    paths: [...configurableProductPaths, ...allPathsExceptVariants, ...simpleProductsPaths],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { notFound: true }

  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const urlKey = params?.url ?? '??'

  const conf = client.query({ query: StoreConfigDocument })
  const productPage = await staticClient.query({
    query: ProductPage2Document,
    variables: { url: 'product/global', urlKey },
  })
  const layout = await staticClient.query({ query: LayoutDocument })

  const product = productPage.data.products?.items?.find((p) => p?.url_key === urlKey)

  if (!product) return { notFound: true }

  let variants = null

  if (product.__typename === 'ConfigurableProduct') {
    variants = await staticClient.query({
      query: variantsQuery,
      variables: { urlKey },
    })
  }

  if (!product) return redirectOrNotFound(staticClient, conf, params, locale)

  const category = productPageCategory(product)
  const up =
    category?.url_path && category?.name
      ? { href: `/${category.url_path}`, title: category.name }
      : { href: `/`, title: 'Home' }

  const revalidate = getRevalidateTime()
  return {
    props: {
      ...defaultConfigurableOptionsSelection(urlKey, client, productPage.data),
      ...layout.data,
      apolloState: await conf.then(() => client.cache.extract()),
      up,
      variants: variants?.data?.products || null,
    },
    revalidate,
  }
}
